import { NAV_SECTIONS, SEARCH_ONLY_PAGES } from '../navigation/navConfig';

/**
 * The name of the page at a given path.
 *
 * Used for `document.title` and for the announcement made when the route
 * changes. Both need the same string, and there was no source for it: 191
 * routes and a `<title>` of "Dietech Software" that never changed.
 *
 * Route configs do carry a `feature` label, but it names a PERMISSION rather
 * than a page — `/people`, `/people/deleted` and `/people/:id` are all
 * "Residents" — so it cannot distinguish the pages a user moves between.
 *
 * Most paths prettify correctly from their last segment
 * (`/ordering/order-guides` → "Order Guides"), so only the ones where
 * that is wrong are listed. Keeping the map to exceptions is what stops it
 * drifting out of date as routes are added: a new route gets a reasonable name
 * for free, and only a bad name needs a line here.
 */

/** Facility-configurable words. The app calls residents whatever the site does. */
export interface PageTitleLabels {
  resident?: string;
  residentPlural?: string;
}

/**
 * Exceptions only, longest-first at lookup time. `:param` segments match any
 * single segment.
 */
const OVERRIDES: Record<string, string> = {
  // The Reports pages without a tab row name themselves in the title and breadcrumb.
  '/reports/print-queue': 'Print Queue',
  '/reports/scheduled': 'Scheduled Reports',
  // Setup's page listing the setup pages (navigation/SectionHome.tsx).
  '/settings': 'Setup',
  // Kitchen's page listing the kitchen pages (the same component).
  '/kitchen-home': 'Kitchen',
  '/meal-patterns': 'Meal Pattern Groups',
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/corporate-dashboard': 'Corporate Dashboard',
  '/overview': 'Global Overview',
  '/people': '{residentPlural}',
  '/people/new': 'New {resident}',
  '/people/:id': '{resident} Details',
  '/people/deleted': 'Manage {residentPlural}',
  '/people/guests/profile/new': 'New Guest Profile',
  '/people/guests/profile/:id': 'Guest Profile',
  '/menucycle-hub': 'Menu Cycle Hub',
  '/menucycle/aim': 'Auto Item Mods',
  '/menucycle/recyclebin': 'Deleted menus',
  // A saved vendor import is New Menu's import tab.
  '/menucycle/new/import/:draftId': 'New Menu',
  '/menucycle/new/import/:draftId/:stage': 'New Menu',
  '/menucycle/excel-diet-orders': 'Excel Diet Orders',
  '/menucycle/workbench/:menuHeaderId': 'Modify',
  '/menucycle/basic/:menuHeaderId': 'Basic Menu',
  '/menucycle/today': "Today's Menu",
  '/emr': 'EMR',
  '/fhir': 'FHIR',
  '/fhir/terms': 'FHIR Terms',
  '/pdpm': 'Nutrition Compliance',
  '/system-log': 'System Log',
  '/user-settings': 'My Settings',
  '/tables/:tableKey': 'Tables',
  '/tables': 'Tables',
  '/items/:id/edit': 'Edit Item',
  '/items/:id': 'Item',
  '/items/new': 'New Item',
  '/items/usda-matcher': 'USDA Matcher',
  '/items/linking': 'Item Linking',
  '/recipes/:id/edit': 'Edit Recipe',
  '/recipes/:id': 'Recipe',
  '/recipes/new': 'New Recipe',
  '/ordering/edi': 'EDI',
  '/ordering/catalog': 'Price Updates',
  '/ordering/pricing-import': 'Price Updates',
  '/ordering/setup': 'Purchasing Setup',
  '/ordering/receiving/:poId': 'Receiving',
  '/ordering/orders/:id': 'Purchase Order',
  '/ordering/invoices/:id': 'Vendor Invoice',
  '/ordering/credit-memos/:id': 'Credit Memo',
  '/ordering/requisitions/:id': 'Requisition',
  '/roles/:id/usage': 'Role Usage',
  '/roles/:id/edit': 'Edit Role',
  '/roles/:id': 'Role',
  '/roles/new': 'New Role',
  '/users/:id': 'User',
  '/users/new': 'New User',
  '/users/deleted': 'Deleted Users',
  '/users/logged-in': 'Logged In Users',
  '/admin/feedback': 'Feedback',
  '/admin/status': 'System Status',
};

/**
 * A menu page's own label (navConfig.ts), so the tab says what the menu and the breadcrumb say:
 * /user-settings is "My Settings", /credit-process "Credits". Exact routes only; a page whose
 * label a facility words its own way is in OVERRIDES above, with its token.
 */
const NAV_LABELS: Record<string, string> = Object.fromEntries([
  ...NAV_SECTIONS.flatMap((s) => s.groups.flatMap((g) => g.pages))
    .filter((p) => !p.route.includes('{') && !p.crossListed)
    .map((p) => [p.route, p.label] as const),
  ...SEARCH_ONLY_PAGES.map((p) => [p.route, p.label] as const),
]);

/** "waste-analysis" → "Waste Analysis". Acronyms already cased are left alone. */
function prettify(segment: string): string {
  return segment
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => (w === w.toUpperCase() && w.length > 1 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/** Does a `/a/:b/c` pattern match a concrete path? */
function patternMatches(pattern: string, parts: string[]): boolean {
  const pp = pattern.split('/').filter(Boolean);
  if (pp.length !== parts.length) return false;
  return pp.every((seg, i) => seg.startsWith(':') || seg === parts[i]);
}

export function resolvePageTitle(pathname: string, labels: PageTitleLabels = {}): string {
  const resident = labels.resident || 'Resident';
  const residentPlural = labels.residentPlural || 'Residents';
  const fill = (s: string) => s
    .replace(/\{residentPlural\}/g, residentPlural)
    .replace(/\{resident\}/g, resident);

  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return fill(OVERRIDES['/']);
  const path = '/' + parts.join('/');

  // A path named exactly here, then a menu page's own label. Both before the
  // `:param` patterns: a menu page is never the `:id` of the detail route
  // beside it (`/items/:id` titled Food Data > Order Guide/Shelf Assignment
  // "Item", and `/people/:id` titled Guests "Resident Details").
  if (OVERRIDES[path]) return fill(OVERRIDES[path]);
  const navLabel = NAV_LABELS[path];
  if (navLabel) return navLabel;

  // Fewest parameters first, so `/items/:id/edit` is not taken by a pattern
  // with more of its segments left open.
  const candidates = Object.keys(OVERRIDES)
    .filter((p) => p.includes(':') && patternMatches(p, parts))
    .sort((a, b) => {
      const paramsA = (a.match(/:/g) ?? []).length;
      const paramsB = (b.match(/:/g) ?? []).length;
      return paramsA - paramsB;
    });
  if (candidates.length > 0) return fill(OVERRIDES[candidates[0]]);

  // Fall back to the last segment that is not an id. A trailing numeric or
  // uuid-ish segment names nothing, so the segment before it is the page.
  const meaningful = [...parts].reverse().find((p) => !/^[0-9a-f-]{6,}$/i.test(p) && !/^\d+$/.test(p));
  return meaningful ? prettify(meaningful) : 'Dietech';
}
